import { Tooltip } from '@mui/material';
import { ReactComponent as CancelIcon } from 'assets/images/cancel.svg';
import { Loader } from 'components/common/Loader';
import Modal from 'components/common/Modal/Modal';
import SearchSelect from 'components/SearchSelect';
import { getInviteStatus, userSearchOptions } from 'constants/constants';
import { useFormik } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import { Badge, Button, Card, Col, Form, Row } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import ReactPaginate from 'react-paginate';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  useDeleteTeamMutation,
  useGetAllMembersMutation,
} from 'redux/services/teams';
import './Users.scss';

interface PaginationResponse {
  limit: number;
  page: number;
  totalCount: number;
  totalPage: number;
}

const Users = () => {
  const { t } = useTranslation();
  const [getAllMembers, { isLoading }] = useGetAllMembersMutation();
  const [teamsList, setTeamsList] = useState([]);
  const [show, setShow] = useState(false);
  const [deleteMemberEmail, setDeleteMemberEmail] = useState<string>('');
  const [deleteTeam, { isLoading: isDeleteLoading }] = useDeleteTeamMutation();
  const [pageLimit, setPageLimit] = useState<string>('10');
  const [deleteProjectId, setDeleteProjectId] = useState('');
  const [paginationData, setPaginationData] = useState<PaginationResponse>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const formik = useFormik({
    initialValues: {
      search_option: 'project',
      search_value: '',
      page_limit: '',
    },
    onSubmit: () => {},
  });

  const filteredUsers = useMemo(() => {
    return teamsList.filter((user: any) => {
      const regEx = new RegExp(formik.values.search_value, 'i');
      const statusRegex = new RegExp(formik.values.search_option, 'i');
      if (formik.values.search_option !== 'project') {
        return (
          (user.email.match(regEx) ||
            user.projectName.match(regEx) ||
            user.projectId.match(regEx) ||
            user.role.match(regEx)) &&
          user.status.match(statusRegex)
        );
      } else {
        return (
          user.email.match(regEx) ||
          user.projectName.match(regEx) ||
          user.projectId.match(regEx) ||
          user.status.match(regEx)
        );
      }
    });
  }, [formik.values.search_option, formik.values.search_value, teamsList]);

  const getTeamsList = async () => {
    const queryParams = {
      limit: searchParams.get('limit'),
    };
    const res: any = await getAllMembers(queryParams);
    if (res.data) {
      setTeamsList(res.data.users);
      setPaginationData(res.data.pagination);
      setPageLimit(res.data.pagination.limit);
    }
  };

  const deleteTeamMember = async () => {
    const body = {
      projectId: deleteProjectId,
      values: {
        email: deleteMemberEmail,
      },
    };
    const res: any = await deleteTeam(body);
    if (res.data) {
      handleClose();
      getTeamsList();
      toast.success('Deleted');
    } else toast.error(res.error.data.message);
  };

  useEffect(() => {
    getTeamsList();
  }, []);

  return (
    <>
      <div className="all-users teams d-flex flex-column h-100">
        <div className="page-title pb-3">
          <h2 className="mb-0">{t('header_users')}</h2>
          <p className="mb-0">{t('users_tagline')} </p>
        </div>
        <Card className="border-0 flex-grow-1 overflow-auto">
          {isLoading && <Loader />}
          <Card.Body className="d-flex flex-column h-100 overflow-auto pageBg">
            <div className="step-wrapper w-100 h-100 ">
              <Row className="mb-3">
                <Col className="max-w-content">
                  <SearchSelect
                    options={userSearchOptions}
                    value={formik.values.search_option}
                    onChange={(val) => {
                      formik.setFieldValue('search_option', val.value);
                    }}
                    name="search_option"
                  />
                </Col>
                <Col>
                  <Form.Control
                    placeholder={t('input_search_placeholder')}
                    name="search_value"
                    value={formik.values.search_value}
                    onChange={(e) => {
                      formik.setFieldValue('search_value', e.target.value);
                    }}
                  />
                </Col>
              </Row>
              <div className="members-list d-flex flex-column flex-grow-1  flex-md-row flex-md-wrap gap-3">
                {filteredUsers.map((list: any, index: number) => (
                  <Card key={index} className="team-member">
                    <Card.Body className="px-0 py-3">
                      <div className="d-flex align-items-start team-member-info w-100">
                        <div className="d-flex px-3 align-items-center pb-2 flex-grow-1 overflow-auto">
                          <img
                            src={`https://ui-avatars.com/api/?size=50&color=fff&rounded=true&bold=true&background=random&name=${
                              list?.name || list.email || 'User'
                            }`}
                            alt=""
                          />
                          <div className="ms-3 d-flex flex-column flex-grow-1 overflow-auto">
                            <h3 className="mb-0">
                              {list.name ?? list.email.split('@')[0] ?? 'User'}
                            </h3>
                            <p className="mb-0">{list?.email}</p>
                            <div>
                              <Badge
                                bg={
                                  list.status.toLowerCase() === 'joined'
                                    ? 'success'
                                    : 'danger'
                                }
                                className={`mt-1 px-1 max-w-content`}
                              >
                                {getInviteStatus[list.status || 'PENDING']}
                              </Badge>
                            </div>
                          </div>
                        </div>
                        {list.role.toLowerCase() !== 'owner' && (
                          <Tooltip
                            title={t('text_delete')}
                            placement="top"
                            arrow
                          >
                            <Button
                              variant="link"
                              className="py-0 ms-auto text-muted"
                              onClick={() => {
                                setDeleteMemberEmail(list.email);
                                setDeleteProjectId(list?.projectId);
                                handleShow();
                              }}
                            >
                              <CancelIcon width="20" />
                            </Button>
                          </Tooltip>
                        )}
                      </div>
                      <div className="px-3 pt-3">
                        <div>
                          <div className="team-project-details">
                            <h3 className="mb-0">
                              {list?.projectName || 'Project name'}
                            </h3>
                            <div className="text-truncate text-muted my-2">
                              {list.projectId}
                            </div>
                          </div>
                          <div className="text-capitalize text-muted">
                            {list?.role.toLowerCase()}
                          </div>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                ))}
                {!isLoading && !teamsList?.length ? (
                  <div className="d-flex justify-content-center w-100 flex-grow-1 align-items-center flex-column">
                    <div className="no-user-placeholder" />
                    <span className="h6 text-center">
                      {t('no_users_added_text')}
                    </span>
                  </div>
                ) : !isLoading && !filteredUsers.length ? (
                  <span className="d-flex justify-content-center align-items-center w-100 h5">
                    {t('no_matching_results')}
                  </span>
                ) : null}
              </div>
            </div>
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center">
                <span>{t('label_rows')} :&nbsp;</span>
                <Form.Select
                  className="max-w-content"
                  value={pageLimit}
                  onChange={(e) => {
                    navigate({
                      pathname: '/users',
                      search: `?page=${paginationData?.page}&limit=${e.target.value}`,
                    });
                    setPageLimit(e.target.value);
                    getTeamsList();
                  }}
                >
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                </Form.Select>
              </div>
              <ReactPaginate
                breakLabel="..."
                nextLabel=">"
                onPageChange={(page) => {
                  navigate({
                    pathname: '/users',
                    search: `?page${page.selected + 1}&limit=${pageLimit}`,
                  });
                  getTeamsList();
                }}
                containerClassName="pagination justify-content-center mb-0"
                previousClassName="page-item"
                previousLinkClassName="page-link"
                nextClassName="page-item"
                nextLinkClassName="page-link"
                pageClassName="page-item"
                pageLinkClassName="page-link"
                activeClassName="active"
                pageRangeDisplayed={5}
                initialPage={0}
                pageCount={paginationData?.totalPage || 1}
                previousLabel="<"
              />
            </div>
          </Card.Body>
        </Card>
      </div>
      <Modal
        show={show}
        title={'modal_title_delete_team_memeber'}
        withIcon={true}
        onClose={handleClose}
        onSubmit={deleteTeamMember}
        cancelBtnText={t('btn_cancel')}
        submitBtnText={t('btn_delete')}
        modalClassName="invite-team-modal"
        modalBodyClassName="pt-0 px-0"
        disabled={isDeleteLoading}
      />
    </>
  );
};

export default Users;
